import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import server from '../../../api/server';
import AdminShell from '../layout/AdminShell';
import Card from '../ui/Card';
import EmptyState from '../ui/EmptyState';
import { CreditCardIcon } from '../ui/icons';

// Redesign of newadmin/Billing.js — same /admin/invoice request and the same
// client-side price constants; total computed with useMemo instead of the
// old setState-during-render.
const PRICES = {
    fact_find: 1.99,
    file_upload: 0.1,
    identification: 7.99,
    bank_statement: 6.99,
    seat: 15,
};

const BillingPage = () => {
    const organisation = useSelector((s) => s.organisation);
    const user = useSelector((s) => s.user);
    const [invoice, setInvoice] = useState(null);

    useEffect(() => {
        const getInvoice = async () => {
            const response = await server.post('/admin/invoice', {
                organisation_id: organisation.id,
                id: user.token,
            });
            if (response.data && response.data.success) {
                setInvoice(response.data.invoice);
            }
        };
        // The old page assumed org + token were already in the store on
        // mount; guard so a hard refresh (store hydrating via checkAuth)
        // fetches once both exist.
        if (organisation.id != null && user.token != null) {
            getInvoice();
        }
    }, [organisation.id, user.token]);

    const rows = useMemo(() => {
        if (invoice == null) return [];
        return [
            { label: 'Fact Finds', qty: invoice.fact_finds, price: PRICES.fact_find },
            { label: 'File Uploads', qty: invoice.file_uploads, price: PRICES.file_upload },
            { label: 'Identifications', qty: invoice.identifications, price: PRICES.identification },
            { label: 'Bank Statements', qty: invoice.bank_statements, price: PRICES.bank_statement },
            { label: 'Agents', qty: invoice.seats, price: PRICES.seat },
        ];
    }, [invoice]);

    const total = useMemo(
        () => rows.reduce((sum, row) => sum + row.qty * row.price, 0).toFixed(2),
        [rows]
    );

    return (
        <AdminShell title="Billing" maxWidth="max-w-4xl">
            <Card
                title="Current Month"
                subtitle="This month's billing breakdown"
                icon={<CreditCardIcon className="h-5 w-5" />}
                bodyClassName="p-0"
            >
                {invoice == null ? (
                    <EmptyState
                        icon={<CreditCardIcon className="h-6 w-6" />}
                        title="Loading invoice..."
                        subtitle="Fetching this month's usage."
                    />
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead>
                                <tr className="border-b border-[#F0F1F5]">
                                    <th className="px-4 sm:px-6 py-3 text-left text-[11px] font-bold uppercase tracking-wide text-[#6B7280]">Items</th>
                                    <th className="px-4 py-3 text-right text-[11px] font-bold uppercase tracking-wide text-[#6B7280]">Qty</th>
                                    <th className="px-4 py-3 text-right text-[11px] font-bold uppercase tracking-wide text-[#6B7280]">Unit Price</th>
                                    <th className="px-4 sm:pr-6 py-3 text-right text-[11px] font-bold uppercase tracking-wide text-[#6B7280]">Amount</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-[#F0F1F5]">
                                {rows.map((row) => (
                                    <tr key={row.label}>
                                        <td className="px-4 sm:px-6 py-4 text-sm font-semibold text-[#1F2937]">{row.label}</td>
                                        <td className="px-4 py-4 text-right text-sm text-[#1F2937]">{row.qty}</td>
                                        <td className="px-4 py-4 text-right text-sm text-[#6B7280]">${row.price.toFixed(2)}</td>
                                        <td className="px-4 sm:pr-6 py-4 text-right text-sm font-bold text-[#5B3FE0]">
                                            ${(row.qty * row.price).toFixed(2)}
                                        </td>
                                    </tr>
                                ))}
                                <tr className="bg-[#FAFAFC]">
                                    <td className="px-4 sm:px-6 py-4 text-sm font-bold text-[#1F2937]" colSpan={3}>
                                        Total
                                    </td>
                                    <td className="px-4 sm:pr-6 py-4 text-right text-xl font-extrabold" style={{ color: '#7C5CFC' }}>
                                        ${total}
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                )}
            </Card>
        </AdminShell>
    );
};

export default BillingPage;
